'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import Prism from '@/components/ui/prism';
import BorderGlow from '@/components/ui/border-glow';
import { parseNowrap } from '@/lib/utils/utils';
import useContactUsContext from '@/components/providers/context/contact-us-context';

// JoinAI logo SVG inline
const JoinAILogo = ({ className }: { className?: string }) => (
    <svg
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 160 44"
        fill="none"
    >
        <g clipPath="url(#clip0_3250_2781)">
            <path
                d="M65.6074 40.9077H72.5068V13.5645L65.6074 15.3853V40.9077Z"
                fill="white"
            />
            <path
                d="M65.6074 11.4802L72.5068 9.65928V3.39062L65.6074 5.21152V11.4802Z"
                fill="white"
            />
            <path
                d="M137.507 5.21152V40.907H144.406V3.39062L137.507 5.21152Z"
                fill="white"
            />
            <path
                d="M119.084 11.8616L114.449 26.1541H121.426L119.799 32.1899H112.491L109.669 40.9123H102.77L114.335 5.2168H123.828L135.393 40.9123H128.493L119.084 11.8616Z"
                fill="white"
            />
            <path
                d="M94.8314 15.3848C93.6966 14.9072 92.4476 14.6445 91.1386 14.6445C88.2503 14.6445 85.6623 15.9221 83.915 17.946V15.0207L77.0156 16.8415V40.9072H83.915V26.0893C83.915 23.3908 86.1127 21.1998 88.8328 21.1998C91.5529 21.1998 93.7506 23.3848 93.7506 26.0893V40.9072H100.65V24.1012C100.65 20.1789 98.2481 16.8117 94.8314 15.3848Z"
                fill="white"
            />
            <path
                d="M47.2628 14.6504C39.751 14.6504 33.6562 20.7041 33.6562 28.1788C33.6562 35.6534 39.751 41.7071 47.2628 41.7071C54.7746 41.7071 60.8753 35.6414 60.8753 28.1788C60.8753 20.7161 54.7806 14.6504 47.2628 14.6504ZM47.3829 35.325C43.8281 35.325 40.9399 32.131 40.9399 28.1788C40.9399 24.2265 43.8281 21.0205 47.3829 21.0205C50.9376 21.0205 53.8199 24.2265 53.8199 28.1788C53.8199 32.131 50.9376 35.325 47.3829 35.325Z"
                fill="white"
            />
            <path
                d="M147.991 31.7009C147.684 31.6293 147.414 31.4084 147.3 31.0979C147.102 30.5785 147.36 30.0114 147.871 29.8084C153.491 27.6352 158.367 24.1666 158.415 24.1308C158.865 23.8143 159.484 23.9099 159.814 24.3636C160.132 24.8114 160.036 25.4263 159.58 25.7546C159.376 25.8979 154.458 29.4203 148.591 31.677C148.387 31.7367 148.177 31.7546 147.991 31.7009Z"
                fill="white"
            />
            <path
                d="M147.78 35.6894C147.522 35.6118 147.318 35.3909 147.246 35.1103C147.162 34.6924 147.426 34.2804 147.847 34.1969C152.23 33.2894 156.079 31.4148 156.121 31.4028C156.505 31.2118 156.968 31.373 157.16 31.7551C157.352 32.1371 157.19 32.5968 156.805 32.7879C156.637 32.8715 152.734 34.758 148.159 35.7073C148.027 35.7252 147.895 35.7192 147.774 35.6894H147.78Z"
                fill="white"
            />
            <path
                d="M147.553 38.6744C147.36 38.5789 147.234 38.3879 147.234 38.1491C147.234 37.8088 147.499 37.5401 147.829 37.5222C149.342 37.4923 150.741 37.0983 150.753 37.1043C151.077 37.0088 151.42 37.2058 151.504 37.5222C151.6 37.8446 151.402 38.1849 151.083 38.2685C151.017 38.2864 149.522 38.6983 147.859 38.7401C147.757 38.752 147.649 38.7282 147.559 38.6804L147.553 38.6744Z"
                fill="white"
            />
            <path
                d="M32.4074 21.397L28.7325 20.7761L28.7445 19.1343L32.1732 18.3522C32.6175 18.2507 32.8878 17.8089 32.7857 17.3671C32.6896 16.9194 32.2453 16.6507 31.8069 16.7522L28.7506 17.4507V1.47461L20.332 5.72536L18.0983 6.29849L9.63173 8.50147L3.62708 4.65073L3.525 22.5194L0.53468 23.6418C0.10835 23.797 -0.101813 24.2746 0.0483034 24.6985C0.180406 25.0328 0.486643 25.2298 0.822904 25.2298C0.918978 25.2298 1.02106 25.2119 1.11713 25.1701L3.75318 24.1731C3.95133 24.8418 4.25156 25.4448 4.64787 25.9821L2.66033 27.9403C2.33608 28.2567 2.33608 28.7761 2.64832 29.1045C2.81645 29.2597 3.02661 29.3433 3.23677 29.3433C3.44694 29.3433 3.6511 29.2656 3.81923 29.1164L5.79476 27.1582C6.79753 27.9343 8.05251 28.394 9.39155 28.394C9.88393 28.394 10.3883 28.3283 10.8927 28.203L15.3542 27.0388L18.9089 26.1074L19.9177 25.8448C19.9177 25.8448 18.6747 35.2716 6.05296 35.6776V42.5313H6.40723C18.6868 42.4597 28.7025 36.2567 28.7025 23.5582V22.4418L32.1252 23.0149C32.1672 23.0149 32.2212 23.0268 32.2633 23.0268C32.6596 23.0268 33.0139 22.7462 33.0739 22.3462C33.152 21.8925 32.8517 21.4686 32.4014 21.403L32.4074 21.397ZM10.3523 20.4955C8.98323 20.4955 7.87237 19.3791 7.88438 18.0119C7.88438 16.6507 9.00725 15.5582 10.3823 15.5642C11.7514 15.5761 12.8562 16.6806 12.8442 18.0477C12.8442 19.4089 11.7214 20.5074 10.3523 20.4955ZM19.9177 18.3761C18.5486 18.3761 17.4438 17.2657 17.4498 15.8985C17.4618 14.5373 18.5787 13.4388 19.9477 13.4448C21.3168 13.4567 22.4277 14.5671 22.4156 15.9343C22.4156 17.2955 21.2928 18.388 19.9177 18.3821V18.3761Z"
                fill="white"
            />
        </g>
        <defs>
            <clipPath id="clip0_3250_2781">
                <rect width="160" height="44" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

export default function DeliversOutcomes() {
    const { t } = useTranslation('delivers-outcomes');
    const { openForm, closeForm } = useContactUsContext();
    return (
        <section className="relative w-full overflow-hidden bg-black pb-[40px] pb-[60px] pb-[80px]">
            <div
                className="m-mobile:w-[87.5vw] m-tablet:max-w-[87.5vw] rounded-b-0 m-mobile:rounded-b-[16px] relative mx-auto flex w-[100vw] max-w-[1440px] flex-col items-center justify-center
                overflow-hidden border-x border-b border-transparent bg-black [background:linear-gradient(#000,#000)_padding-box,linear-gradient(180deg,rgba(106,105,108,0)_0%,rgba(106,105,108,0.8)_100%)_border-box]"
            >
                {/* Background */}
                <div className="absolute inset-0 bg-transparent">
                    <Prism
                        height={4}
                        baseWidth={3}
                        animationType="rotate"
                        glow={0.9}
                        noise={0.2}
                        transparent
                        scale={3.1}
                        hueShift={0}
                        colorFrequency={1}
                        hoverStrength={2}
                        inertia={0.05}
                        bloom={1}
                        timeScale={0.5}
                        suspendWhenOffscreen
                    />
                </div>

                {/* Content */}
                <div className="m-mobile:w-[87.5vw] m-tablet:max-w-[87.5vw] tablet:pt-[80px] desktop:pt-[120px] tablet:gap-[160px] desktop:gap-[200px] relative z-10 flex w-[100vw] max-w-[1440px] flex-col items-center gap-[120px] pt-[60px]">
                    {/* Top descriptive text */}
                    <div className="j-h5 tablet:items-center tablet:gap-[32px] tablet:text-center flex w-[87.5%] flex-col items-start gap-[24px] text-start">
                        {/* White paragraph */}
                        <div className="text-white">
                            <p>{t('topText.part1')}</p>
                            <p>{t('topText.part2')}</p>
                            <p>{t('topText.part3')}</p>
                            <p>{t('topText.part4')}</p>
                        </div>

                        {/* Gradient highlight text */}
                        <div
                            className="bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]"
                            style={{
                                background:
                                    'linear-gradient(90deg, #8365ff 0%, #f873ff 100%)',
                                WebkitBackgroundClip: 'text',
                            }}
                        >
                            <p>{t('highlightText.part1')}</p>
                            <p>{parseNowrap(t('highlightText.part2'))}</p>
                        </div>
                    </div>

                    {/* Bottom: Logo + Main Title + CTA */}
                    <div className="tablet:items-center tablet:pb-[160px] desktop:pb-[200px] flex w-[87.5%] flex-col items-start pb-[120px]">
                        {/* JoinAI Logo */}
                        <JoinAILogo className="tablet:h-[44px] tablet:text-center h-[20px] w-auto text-start" />

                        {/* Main Title */}
                        <p className="j-h4 tablet:j-h2 tablet:text-center tablet:pb-[48px] pb-[32px] pt-[16px] text-start text-white">
                            {parseNowrap(t('mainTitle'))}
                        </p>

                        {/* CTA Button */}
                        <BorderGlow
                            edgeSensitivity={7}
                            glowColor="40 80 80"
                            backgroundColor="transparent"
                            borderRadius={40}
                            glowRadius={2}
                            glowIntensity={1}
                            coneSpread={25}
                            animated={false}
                            colors={['#c084fc', '#f472b6', '#38bdf8']}
                            fillOpacity={0}
                            enableFloatingShadow={false}
                            borderWidth={2}
                            baseBorderColors={[
                                '#C1B1ED 0%',
                                '#7F62D3 23.5%',
                                '#537CF7 53.15%',
                                '#E356FD 77.87%',
                                '#FBDE7A 100%',
                            ]}
                            onClick={openForm}
                        >
                            <div className="j-l1 w-auto px-[24px] py-[12px] text-white">
                                {t('cta')}
                            </div>
                        </BorderGlow>
                    </div>
                </div>
            </div>
        </section>
    );
}
